import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, Maximize2, Upload, Sparkles, Video, X, Menu, Clock, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/App";
import Footer from "@/components/Footer";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

// Hero Before/After Carousel Component
function HeroCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const videoRef = useRef(null);

  const slides = [
    { before: "/images/before-1.jpg", after: "/videos/after-1.mp4", label: "Exterior" },
    { before: "/images/before-2.jpg", after: "/videos/after-2.mp4", label: "Kitchen" },
    { before: "/images/before-3.jpg", after: "/videos/after-3.mp4", label: "Living Room" },
    { before: "/images/before-4.jpg", after: "/videos/after-4.mp4", label: "Bedroom" },
    { before: "/images/before-5.jpg", after: "/videos/after-5.mp4", label: "Bathroom" }
  ];

  useEffect(() => {
    let loadedCount = 0;
    slides.forEach((slide) => {
      const img = new window.Image();
      img.src = slide.before;
      img.onload = () => { loadedCount++; if (loadedCount >= slides.length) setAllLoaded(true); };
      img.onerror = () => { loadedCount++; if (loadedCount >= slides.length) setAllLoaded(true); };
    });
    slides.forEach((slide) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.src = slide.after;
      video.load();
    });
    const timeout = setTimeout(() => setAllLoaded(true), 3000);
    return () => clearTimeout(timeout);
  }, []);

  const goToPrev = () => { setShowVideo(false); setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length); };
  const goToNext = () => { setShowVideo(false); setCurrentIndex((prev) => (prev + 1) % slides.length); };

  if (!allLoaded) {
    return (
      <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl aspect-[16/9] bg-slate-200">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-slate-300 border-t-[#3C59B2] rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl aspect-[16/9]">
        {!showVideo ? (
          <img src={slides[currentIndex].before} alt={`${slides[currentIndex].label} before`} className="w-full h-full object-cover" />
        ) : (
          <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline loop>
            <source src={slides[currentIndex].after} type="video/mp4" />
          </video>
        )}
        {/* Badges hidden on mobile for performance */}
        <div className={`hidden md:block absolute top-4 left-4 ${showVideo ? 'bg-[#3C59B2]' : 'bg-slate-900/80 backdrop-blur-sm'} text-white rounded-full px-5 py-2 text-sm font-semibold uppercase tracking-wide`}>
          {showVideo ? 'After' : 'Before'}
        </div>
        <div className="hidden md:block absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-slate-700 rounded-full px-4 py-2 text-sm font-medium">
          {slides[currentIndex].label}
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 mt-6">
        <button onClick={goToPrev} className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md flex items-center justify-center transition-all">
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex bg-slate-100 rounded-full p-1">
          <button onClick={() => setShowVideo(false)} className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${!showVideo ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
            Before
          </button>
          <button onClick={() => { setShowVideo(true); if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }}} className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${showVideo ? 'bg-[#3C59B2] shadow-sm text-white' : 'text-slate-500 hover:text-slate-700'}`}>
            After
          </button>
        </div>
        <button onClick={goToNext} className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md flex items-center justify-center transition-all">
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, index) => (
          <button key={index} onClick={() => { setCurrentIndex(index); setShowVideo(false); }} className={`w-2 h-2 rounded-full transition-all ${index === currentIndex ? "bg-[#3C59B2] w-6" : "bg-slate-300 hover:bg-slate-400"}`} />
        ))}
      </div>
    </div>
  );
}

// Portfolio Video Component
function PortfolioVideo({ src, poster, label }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasError, setHasError] = useState(false);

  const togglePlay = () => { 
    if (videoRef.current && !hasError) { 
      if (isPlaying) { 
        videoRef.current.pause(); 
      } else { 
        videoRef.current.play().catch(() => setHasError(true)); 
      } 
      setIsPlaying(!isPlaying); 
    } 
  };
  const toggleMute = () => { if (videoRef.current) { videoRef.current.muted = !isMuted; setIsMuted(!isMuted); } };
  const toggleFullscreen = () => {
    const videoEl = videoRef.current;
    const containerEl = containerRef.current;
    if (!videoEl || hasError) return;

    const doc = document;
    const isFs =
      doc.fullscreenElement ||
      // @ts-ignore - iOS Safari
      doc.webkitFullscreenElement;

    if (!isFs) {
      // iOS Safari native fullscreen
      // @ts-ignore
      if (typeof videoEl.webkitEnterFullscreen === "function") {
        try {
          // @ts-ignore
          videoEl.webkitEnterFullscreen();
          return;
        } catch {
          // ignore
        }
      }
      const target = containerEl || videoEl;
      if (target.requestFullscreen) {
        target.requestFullscreen().catch(() => {});
      }
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else {
        // @ts-ignore
        doc.webkitExitFullscreen?.();
      }
    }
  };

  return (
    <div ref={containerRef} className="relative rounded-2xl overflow-hidden shadow-lg group bg-slate-900">
      <video ref={videoRef} className="w-full aspect-video object-cover" loop muted playsInline preload="metadata" poster={poster} onError={() => setHasError(true)}>
        <source src={src} type="video/mp4" />
      </video>
      {/* Big Play Button in Center */}
      {!isPlaying && (
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity" aria-label="Play video">
          <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-slate-700 ml-1" />
          </div>
        </button>
      )}
      {/* Controls when playing - always visible on mobile, on hover on desktop */}
      <div
        className={`absolute bottom-4 right-4 flex gap-2 transition-opacity ${
          isPlaying
            ? 'opacity-100 md:opacity-0 md:group-hover:opacity-100'
            : 'opacity-0'
        }`}
      >
        <button onClick={togglePlay} className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 transition-all shadow-lg hover:scale-105">
          {isPlaying ? <Pause className="w-5 h-5 text-slate-700" /> : <Play className="w-5 h-5 text-slate-700" />}
        </button>
        <button onClick={toggleMute} className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 transition-all shadow-lg hover:scale-105">
          {isMuted ? <VolumeX className="w-5 h-5 text-slate-700" /> : <Volume2 className="w-5 h-5 text-slate-700" />}
        </button>
      </div>
      {label && (
        <span className="absolute bottom-4 left-4 text-xs text-white/70 font-medium">{label}</span>
      )}
    </div>
  );
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Force scroll en haut au montage de la page
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const handleGetStarted = (service) => {
    if (user) {
      navigate("/order", { state: { service, isB2B: true } });
    } else {
      navigate("/signup", { state: { redirectTo: "/order", service, isB2B: true } });
    }
  };

  const faqs = [
    { q: "How does the B2B partnership work?", a: "You submit your client's photos through our platform, we create the videos, and deliver them white-labeled with your client's branding. You can resell at your own prices." },
    { q: "What's the turnaround time?", a: "Cliptour (furnished properties): 24 business hours. Cliptour Staged (empty properties): 48 business hours." },
    { q: "Can I use my client's logo?", a: "Yes! We offer white-label service. Your client's logo will appear on the final videos." },
    { q: "What file formats do you deliver?", a: "We deliver HD MP4 files (1080p 60fps, 16:9 aspect ratio), optimized for social media, MLS, and web use." },
    { q: "Is there a minimum order volume?", a: "No minimum orders required. However, we offer custom pricing for partners with 5+ properties per month." },
    { q: "How do revisions work?", a: "2 revisions are included with every order. Additional revisions are $50 each." }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="partners-page">
      {/* Promo Banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-[#3C59B2] text-white py-2.5">
          <div className="container-custom flex items-center justify-center gap-4">
            <span className="text-sm font-medium">
              <Sparkles className="inline w-4 h-4 mr-2" />
              $100 off your first order
            </span>
            <button 
              onClick={() => setShowBanner(false)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Partners Header */}
      <header className={`fixed left-0 right-0 z-50 glass border-b border-slate-100 ${showBanner ? 'top-10' : 'top-0'}`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/partners" className="flex items-center gap-2" onClick={() => window.scrollTo(0, 0)}>
              <span className="text-xl lg:text-2xl font-bold text-slate-900">Cliptour</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-sm font-medium text-slate-600 hover:text-slate-900 cursor-pointer">Pricing</a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-sm font-medium text-slate-600 hover:text-slate-900 cursor-pointer">How it works</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-sm font-medium text-slate-600 hover:text-slate-900 cursor-pointer">FAQ</a>
              <Link to="/contact" state={{ fromPartners: true }} onClick={() => window.scrollTo(0, 0)} className="text-sm font-medium text-slate-600 hover:text-slate-900">Contact</Link>
            </nav>
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" className="text-slate-600 hover:text-slate-900">Log in</Button></Link>
                  <Link to="/signup"><Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">Sign up</Button></Link>
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-900" />
              ) : (
                <Menu className="w-6 h-6 text-slate-900" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <nav className="flex flex-col gap-4">
                <a
                  href="#pricing"
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-sm font-medium text-slate-600 py-2"
                >
                  Pricing
                </a>
                <a
                  href="#how-it-works"
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-sm font-medium text-slate-600 py-2"
                >
                  How it works
                </a>
                <a
                  href="#faq"
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-sm font-medium text-slate-600 py-2"
                >
                  FAQ
                </a>
                <Link
                  to="/contact"
                  state={{ fromPartners: true }}
                  onClick={() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }}
                  className="text-sm font-medium text-slate-600 py-2"
                >
                  Contact
                </Link>
                <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-100">
                  {user ? (
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-slate-900 text-white rounded-full">
                        Dashboard
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/login" state={{ isB2B: true }} onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full rounded-full">
                          Log in
                        </Button>
                      </Link>
                      <Link to="/signup" state={{ isB2B: true }} onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full bg-slate-900 text-white rounded-full">
                          Sign up
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className={`py-12 lg:py-16 ${showBanner ? 'pt-44 lg:pt-52' : 'pt-36 lg:pt-44'}`} data-testid="partners-hero">
        <div className="container-custom">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Partner with Cliptour
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-slate-600 mx-auto mb-4 text-center px-4 max-w-3xl">
              Static photos become cinematic. Offer your clients premium video production without the production hassle. Special rates for photographers and agencies.
            </p>
            <p className="text-[#3C59B2] font-semibold mb-8">
              AI powered, human storytelling.
            </p>

            <div className="flex justify-center mb-12">
              <Button 
                onClick={() => handleGetStarted("cliptour_staged")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                Get Started
              </Button>
            </div>
          </motion.div>

          {/* Hero Carousel */}
          <motion.div 
            className="mt-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <HeroCarousel />
          </motion.div>
        </div>
      </section>

      {/* See Our Work */}
      <section className="py-12 lg:py-16">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            See our work
          </motion.h2>
          <div className="space-y-8">
            <PortfolioVideo src="https://pub-f769c896ded6405a9b5fd7301d2d91a2.r2.dev/portfolio-1.mp4" poster="/images/poster-1.jpg" label="Cliptour Staged" />
            <PortfolioVideo src="https://pub-f769c896ded6405a9b5fd7301d2d91a2.r2.dev/portfolio-2.mp4" poster="/images/poster-2.jpg" label="Cliptour Staged" />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-12 lg:py-16 bg-slate-50" id="how-it-works">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            How it works
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: "1", icon: Upload, title: "Upload your photos", desc: "Send all property photos — we'll select the best 10 for staging and animation" },
              { step: "2", icon: Sparkles, title: "We work our magic", desc: "We select, stage, animate & edit your photos into cinematic video" },
              { step: "3", icon: Video, title: "Receive your video", desc: "Get your clips and montage in 24-48 business hours, ready to post" }
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="w-16 h-16 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <item.icon className="w-7 h-7 text-slate-700" />
                </div>
                <div className="text-sm font-semibold text-[#3C59B2] mb-2">Step {item.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Pricing */}
      <section className="py-12 lg:py-16" id="pricing">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            B2B Partner Pricing
          </motion.h2>
          <p className="text-center text-slate-600 mb-16">2 revisions included. $50 per additional revision.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Cliptour B2B */}
            <motion.div
              className="card-base flex flex-col"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Cliptour</h3>
                <p className="text-slate-600">Already furnished?</p>
              </div>
              
              <div className="mb-6">
                <span className="text-slate-400 line-through text-lg">$300</span>
                <span className="text-4xl font-bold text-slate-900 ml-2">$200</span>
                <span className="text-slate-600 text-sm ml-1">first order</span>
              </div>

              <ul className="space-y-3 mb-6 flex-grow">
                {["10 video clips with lifestyle characters", "1 full property tour with music", "Smooth cinematic camera movements"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Clock className="w-3 h-3" /> 24h delivery
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Film className="w-3 h-3" /> HD video files
                </span>
              </div>
              
              <Button 
                onClick={() => handleGetStarted("cliptour")}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
              >
                Get Started
              </Button>
            </motion.div>

            {/* Cliptour Staged B2B */}
            <motion.div
              className="card-base flex flex-col relative border-[#3C59B2]/20 ring-1 ring-[#3C59B2]/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="absolute -top-3 right-6">
                <span className="badge-popular">Most Popular</span>
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Cliptour Staged</h3>
                <p className="text-slate-600">Empty rooms?</p>
              </div>
              
              <div className="mb-6">
                <span className="text-slate-400 line-through text-lg">$550</span>
                <span className="text-4xl font-bold text-slate-900 ml-2">$450</span>
                <span className="text-slate-600 text-sm ml-1">first order</span>
              </div>

              <ul className="space-y-3 mb-4 flex-grow">
                {["10 video clips with lifestyle characters", "1 full property tour with music", "Smooth cinematic camera movements"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
                <li className="flex items-start gap-3 text-[#3C59B2]">
                  <span className="w-5 h-5 flex items-center justify-center font-bold text-[#3C59B2] flex-shrink-0 mt-0.5">+</span>
                  <span className="font-medium">10 photos with virtual furniture & decor</span>
                </li>
              </ul>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Clock className="w-3 h-3" /> 48h delivery
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Film className="w-3 h-3" /> HD video files
                </span>
              </div>
              
              <Button 
                onClick={() => handleGetStarted("cliptour_staged")}
                className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white rounded-full py-6"
              >
                Get Started
              </Button>
            </motion.div>
          </div>

          <p className="text-center text-slate-600 mt-8">
            High volume? <Link to="/contact" state={{ fromPartners: true }} onClick={() => window.scrollTo(0, 0)} className="text-[#3C59B2] font-medium hover:underline">Contact us for custom pricing</Link>
          </p>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16">Why partner with us?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">White Label</h3>
              <p className="text-slate-600">Your client&apos;s logo, your brand. We stay behind the scenes.</p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Fast Turnaround</h3>
              <p className="text-slate-600">24-48 hour delivery means you can offer premium service without delays.</p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Profitable Margins</h3>
              <p className="text-slate-600">B2B pricing gives you healthy margins on every project.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16" id="faq">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-6 data-[state=open]:border-[#3C59B2]/30">
                  <AccordionTrigger className="text-left text-slate-900 font-medium py-5 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 lg:py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80"
            alt="Luxury home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80" />
        </div>
        
        <div className="container-custom text-center relative z-10">
          <motion.h2 
            className="text-3xl lg:text-5xl font-bold mb-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to bring your photos to life?
          </motion.h2>
          <motion.p 
            className="text-slate-300 text-lg mb-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Transform your property photos into cinematic marketing videos.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button 
              onClick={() => handleGetStarted("cliptour_staged")}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              Get Started
            </Button>
            <Link to="/contact" state={{ fromPartners: true }} onClick={() => window.scrollTo(0, 0)}>
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
              >
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}